import PropTypes from 'prop-types';
import { graphql, useFragment } from 'react-relay';
import classNames from 'classnames';

import FavoritesItemProvider from 'dibs-portfolio-folder/exports/FavoritesItemProvider';
import { ItemTile } from 'dibs-search-product-tile/exports/ItemTile';

import { FavoritesItem } from '../../../components/global/FavoriteIcons/FavoritesItem';
import { VERTICAL_OFFSET_RESP_IMG, SRC_SET_SIZES_TILE } from '../../../constants/imageConstants';

import styles from './PdpRespSimilarItem.scss';

const SMALL_TILE_TYPE = 'small';
export const X_SMALL_TILE_TYPE = 'xSmall';

export const PdpRespSimilarItem = ({
    index,
    item: itemRef,
    onClick,
    user: userRef,
    quickView,
    favorites,
    currency,
    lazyLoadStartIndex,
    showLightBox,
    isMobile, // isMobile added to allow component to get mobile quickview styles
    showMeasurements,
    hideTileImagePadding,
    tileType,
    showAuctionBids,
    shippingBadgeBgColor,
}) => {
    const item = useFragment(
        graphql`
            fragment PdpRespSimilarItem_item on Item {
                serviceId
                isFurniture
                ...ItemTile_item @arguments(isTrade: $isTrade)
                ...FavoritesItem_item
                ...FavoritesItemProvider_item
            }
        `,
        itemRef
    );
    const user = useFragment(
        graphql`
            fragment PdpRespSimilarItem_user on User {
                canShowSellerName
                isVerifiedTrade
            }
        `,
        userRef
    );

    const { activeQuickView, openQuickView, closeQuickView } = quickView || {};
    const { canShowSellerName } = user || {};
    const canShowCreatorName = !canShowSellerName && item.isFurniture;

    // Default Layout
    return (
        <div className={classNames(styles.item, { [styles[tileType]]: !!styles[tileType] })}>
            <FavoritesItemProvider item={item} applyMouseEvents>
                {({ isFolderVisible, ...favoritesItemProps }) => {
                    const favoritesComponent = favorites ? (
                        <div
                            className={classNames(styles.favorites, {
                                [styles[tileType]]: !!styles[tileType],
                            })}
                        >
                            <FavoritesItem
                                wrapperClass={styles.favoritesWrapper}
                                heartIconWrapperClass={styles.favoritesIconWrapper}
                                folderIconWrapperClass={styles.favoritesIconWrapper}
                                {...favorites}
                                item={item}
                                itemImage={item.firstPhotoWebPath || ''}
                                isVerifiedTrade={user?.isVerifiedTrade || false}
                                fetchFolder={favorites.fetchFolder && isFolderVisible}
                                {...favoritesItemProps}
                            />
                        </div>
                    ) : null;

                    return (
                        <ItemTile
                            disableContentMargin={tileType === X_SMALL_TILE_TYPE}
                            tileType={tileType === X_SMALL_TILE_TYPE ? 'IMG_LEFT_ALIGN' : null}
                            index={index}
                            item={item}
                            currency={currency}
                            itemsPerRow={lazyLoadStartIndex}
                            showSellerName={canShowSellerName}
                            showCreatorName={canShowCreatorName}
                            onClick={() => onClick(index)}
                            activeQuickView={activeQuickView}
                            openQuickView={openQuickView}
                            closeQuickView={closeQuickView}
                            showQuickView={!!openQuickView && !!closeQuickView}
                            showLightBox={showLightBox}
                            disableBorder
                            useLoFiLazyLoader
                            isMobile={isMobile}
                            imageSize={
                                tileType === X_SMALL_TILE_TYPE ? 'imageXSmall' : 'imageSmall'
                            }
                            itemSearch={null}
                            imageLoadVerticalOffset={VERTICAL_OFFSET_RESP_IMG}
                            viewer={null}
                            srcSetSizes={SRC_SET_SIZES_TILE}
                            showMSKUSwatch={false}
                            favoritesComponent={favoritesComponent}
                            showMeasurements={showMeasurements}
                            hideImagePadding={hideTileImagePadding}
                            showAuctionBids={showAuctionBids}
                            shippingBadgeBgColor={shippingBadgeBgColor}
                        />
                    );
                }}
            </FavoritesItemProvider>
        </div>
    );
};

PdpRespSimilarItem.defaultProps = {
    lazyLoadStartIndex: 0,
    showLightBox: false,
    isMobile: false,
    tileType: SMALL_TILE_TYPE,
};

PdpRespSimilarItem.propTypes = {
    index: PropTypes.number.isRequired,
    item: PropTypes.object.isRequired,
    currency: PropTypes.string,
    onClick: PropTypes.func,
    // @see QuickViewProvider.jsx
    quickView: PropTypes.shape({
        openQuickView: PropTypes.func,
        closeQuickView: PropTypes.func,
        activeQuickView: PropTypes.number,
    }),
    // @see PdpRespFavoritesProvider.jsx
    favorites: PropTypes.shape({
        location: PropTypes.string.isRequired,
        userId: PropTypes.string,
        userIds: PropTypes.arrayOf(PropTypes.string).isRequired,
        itemIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        disable: PropTypes.bool,
        environment: PropTypes.object.isRequired,
        singlePortfolioItem: PropTypes.bool.isRequired,
        loadPortfolioItemData: PropTypes.bool.isRequired,
        heartViewer: PropTypes.object,
        folderViewer: PropTypes.object,
        fetchFolder: PropTypes.bool,
    }),
    lazyLoadStartIndex: PropTypes.number,
    user: PropTypes.object,
    showLightBox: PropTypes.bool,
    isMobile: PropTypes.bool,
    showMeasurements: PropTypes.bool,
    hideTileImagePadding: PropTypes.bool,
    tileType: PropTypes.oneOf([X_SMALL_TILE_TYPE, SMALL_TILE_TYPE]),
    showAuctionBids: PropTypes.bool,
    shippingBadgeBgColor: PropTypes.string,
};
